import React, { useMemo } from 'react';
import { Button } from '@cmsgov/design-system';

import { FormattedMessage } from 'react-intl';

// Components

import DocumentInfo from './DocumentInfo';

// Hooks

import { useUploadModal } from '@hooks/useUploadModal';

// Utils

import { getCategoryValidationRules } from '@utils/miscellaneous/getVerificationDocumentsConfig';
import { preventerTools } from '@utils/tools/preventerTools';
import { omit, pick } from '@utils/miscellaneous/objectHelpers';
import { buildMockUploadResponse } from '@utils/miscellaneous/buildMockUploadResponse';

// Constants

import { configs } from '@constants/configs/configs';
import states from '@constants/configs/states';
import { TYPES_EXCEPTIONS_FOR_LABELS } from '@constants/configs/QLEDocumentVerificationConfigs/documentsVerificationConstants';
import { UPLOADED_DOCUMENT_STATUSES, UPLOAD_MODAL_VIEWS } from '@constants/common/miscellaneous';




const DocumentItem = props => {
    const {
        applicantGuid,
        applicantEventId,
        caseNumber,
        categoryType,
        documentType,
        documents,
        event,
        overrideDone,
        parentActions: {
            handleResponse,
            removeDocument,
            showModal,
            updateMockData,
            uploadDocument,
            viewUploadedDocument
        },
        receivedDocumentTypes,
        stateCode,
        validationCompleted,
        userRole
    } = props;

    const {
        INITIAL,
        REJECTED,
        SUBMITTED,
        VERIFIED
    } = UPLOADED_DOCUMENT_STATUSES;

    const {
        REMOVE_DOCUMENT,
        UPLOAD_DOCUMENT
    } = UPLOAD_MODAL_VIEWS;

    const [ setModalData ] = useUploadModal();

    const isException = useMemo(() => {
        return stateCode === states.NEW_JERSEY && documentType === TYPES_EXCEPTIONS_FOR_LABELS.SPECIAL_CIRCUMSTANCE_ATTESTATION
            ? `${documentType}_SPECIAL_LABEL`
            : null;
    }, [stateCode, documentType]);

    const UPLOAD_FILE_NAME_REGEX = /^[\da-zñáéíóúü @.,-]+$/i;

    const getUploadModalConfig = ({ fileName, relativePath }) => ({
        mainLabel: 'modals.main_label.attach_documents',
        modalHeader: null,
        acceptedFileTypes: 'image/*,.pdf,.doc, .docx, .xls, .xlsx',
        acceptedFileTypesVisualization: 'png, jpg, jpeg, pdf,doc, docx, xls, xlsx',
        excludeFileExtensionList: ['docm', 'xlsm'],
        acceptedFileTypesValidation: ['png', 'jpg', 'jpeg', 'pdf', 'doc', 'docx', 'xls', 'xlsx', 'ms-excel', 'msword'],
        uploadedFileName: null,
        uploadedFileSize: null,
        maxFileSize: Math.pow(2, 20) * 5,
        maxNameLength: 100,
        fileNameValidator: name => UPLOAD_FILE_NAME_REGEX.test(name),
        exceedingSizeWarning: 'modals.file_size_exceeding_warning',
        modalInfoItems: [],
        upload: {
            action: configs.ENV.DEVELOPMENT
                ? formData => {
                    updateMockData('upload', formData, stateCode, applicantGuid);
                    // Local testing only: instead of calling the backend, return a dynamically
                    // built mock response (shaped like the real upload response) so
                    // findEncryptedDocumentPath (UploadView) can resolve the just-uploaded
                    // document instead of failing with `missing_path`.
                    return Promise.resolve(buildMockUploadResponse(formData));
                }
                : formData => uploadDocument(formData).then(res => handleResponse(res)),
            payloadComponents: {
                applicantEventId,
                caseNumber,
            },
        },
        removal: {
            action: configs.ENV.DEVELOPMENT
                ? props => {
                    const { applicantEventId, categoryType, documentType } = props;
                    updateMockData(
                        'remove',
                        {
                            categoryType,
                            documentType,
                        },
                        stateCode,
                        applicantGuid,
                        applicantEventId,
                    );
                    return Promise.resolve({ success: true });
                }
                : props => removeDocument(props).then(res => handleResponse(res)),
            payloadComponents: {
                applicantEventId,
                caseNumber,
                fileName,
                moduleId: localStorage?.HHID || null,
                relativePath,
            },
        },
        view: UPLOAD_DOCUMENT,
    });

    const sorted = useMemo(() => {
        const compare = ({ documentStatus: a }, { documentStatus: b }) =>
            (a === VERIFIED || a === INITIAL || a === SUBMITTED) && b === REJECTED ? -1 : 1;

        return receivedDocumentTypes.sort(compare).reduce((sortedDocuments, doc) => {
            const { documentType: receivedDocumentsDocumentType, documentStatus } = doc;

            return {
                ...sortedDocuments,
                ...(receivedDocumentsDocumentType === documentType
                    ? {
                        [documentStatus]: [
                            ...(sortedDocuments[documentStatus] ? sortedDocuments[documentStatus] : []),
                            doc,
                        ],
                    }
                    : {}),
            };
        }, {});
    }, [receivedDocumentTypes, documentType, VERIFIED, INITIAL, SUBMITTED, REJECTED]);

    const onClickAction = (e, extraParams) => {

        const { view, uploadedFileName, uploadedFileSize, relativePath } = preventerTools.provideObject(extraParams);
        const UPLOAD_MODAL_CONFIG = getUploadModalConfig({
            fileName: uploadedFileName,
            relativePath,
        });

        const types = { categoryType, documentType };

        const getRejectedDocuments = () => {
            const cat = documents.find(c => c.categoryType === categoryType);
            const dt = cat ? cat.documentTypes : null;
            return dt ? dt.map(d => pick(d, [ 'uploadedFileName', 'uploadedFileSize' ])) : [];
        };

        e.preventDefault();

        setModalData({
            config: UPLOAD_MODAL_CONFIG,
            enhancements: [
                {
                    path: 'modalHeader',
                    value: view === REMOVE_DOCUMENT ? null : 'modals.header.document_upload'
                },
                {
                    add: true,
                    path: 'modalInfoItems',
                    value: {
                        label: <FormattedMessage id='modals.upload_document.first_item.header' />,
                        value: <FormattedMessage id={`qle.document.${isException ? isException : documentType}`}/>
                    }
                },
                {
                    path: 'upload.payloadComponents',
                    value: types
                },
                {
                    path: 'removal.payloadComponents',
                    value: types
                },
                {
                    path: 'rejectedDocuments',
                    value: getRejectedDocuments()
                },
                {
                    path: 'uploadedFileName',
                    value: uploadedFileName
                },
                {
                    path: 'uploadedFileSize',
                    value: uploadedFileSize
                },
                ...(
                    view ?
                        [{
                            path: 'view',
                            value: view
                        }] : []
                )
            ]
        });
    };

    const getDocumentInfo = () => {
        return Object.entries(sorted).map(([ status, documents ]) => {
            return (
                <DocumentInfo
                    key={status}
                    {...{
                        documents,
                        documentType,
                        getUploadModalConfig: getUploadModalConfig,
                        onClickAction,
                        parentActions: {
                            showModal,
                            viewUploadedDocument
                        },
                        setModalData,
                        stateCode,
                        status,
                        userRole
                    }}
                />
            );
        });
    };


    const isUploadEnabled = useMemo(
        () => !overrideDone &&
        //@ts-ignore
        Object.values(omit(sorted, [ REJECTED ])).every(t => !t.length),
        [ overrideDone, sorted ]
    );

    const currentCategory = useMemo(
        () => (c => c ? c : { documentTypes: [] })(documents.find(c => c.categoryType === categoryType)),
        [ documents ]
    );

    const { documentsQty, mandatoryDocuments = [], documentTypes } = useMemo(
        () => getCategoryValidationRules(categoryType, stateCode, event),
        [ categoryType, stateCode, event ]
    );

    const isCategoryValidated = useMemo(
        () => !overrideDone &&
            currentCategory.documentTypes
                .filter(d => documentTypes.includes(d.documentType))
                .filter(d =>
                    d.documentStatus === INITIAL ||
                    d.documentStatus === SUBMITTED ||
                    d.documentStatus === VERIFIED
                ).length === documentsQty,
        [ overrideDone, currentCategory, documentsQty ]
    );

    const uploadedDocumentsQty = useMemo(
        () => currentCategory.documentTypes
            .filter(d => documentTypes.includes(d.documentType))
            .filter(d =>
                d.documentStatus === INITIAL ||
                d.documentStatus === SUBMITTED ||
                d.documentStatus === VERIFIED
            ).length,
        [ currentCategory ]
    );

    const isTypeOfMandatoryDocuments = useMemo(
        () => mandatoryDocuments.includes(documentType),
        [ mandatoryDocuments, documentType ]
    );

    const isMandatoryDocumentsAlreadyUploaded = useMemo(
        () =>
            mandatoryDocuments.every(
                mandatoryDocType =>
                    !!currentCategory.documentTypes
                        .filter(d => documentTypes.includes(d.documentType))
                        .find(
                            doc =>
                                doc.documentType === mandatoryDocType &&
                                (
                                    doc.documentStatus === INITIAL ||
                                    doc.documentStatus === SUBMITTED ||
                                    doc.documentStatus === VERIFIED
                                )
                        )
            ),
        [mandatoryDocuments, currentCategory],
    );

    const isButtonDisabled = useMemo(
        () => {
            if(mandatoryDocuments.length === 0) {
                return isCategoryValidated;
            } else {
                switch(true) {
                    case uploadedDocumentsQty === documentsQty - mandatoryDocuments.length:
                        switch(true) {
                            case isTypeOfMandatoryDocuments && isMandatoryDocumentsAlreadyUploaded:
                                return true;
                            case isTypeOfMandatoryDocuments && !isMandatoryDocumentsAlreadyUploaded:
                                return false;
                            case !isTypeOfMandatoryDocuments && isMandatoryDocumentsAlreadyUploaded:
                                return false;
                            default:
                                return true;
                        }
                    case uploadedDocumentsQty < documentsQty - mandatoryDocuments.length:
                        return false;
                    default:
                        return true;
                }
            }
        },
        [ mandatoryDocuments, isCategoryValidated, currentCategory, documentType ]
    );

    const getDocumentTypeLabelId = () => {
        switch(true) {
            case !!(isException && validationCompleted):
                return `qle.document.${isException}`;
            default:
                return `qle.document.${documentType}`;
        }
    };

    return (
        <li id='qle-document-item' className="qle-document-list__item">
            <div className='grid-row qle-document-list__item__header'>
                <div
                    id={`qle-document-desc-${applicantGuid}-${categoryType}-${documentType}`}
                    className='qle-document-list__item__header__description'
                >
                    <FormattedMessage id={getDocumentTypeLabelId()} />
                </div>
                <div className='qle-document-list__item__header__action'>
                    {
                        !validationCompleted && isUploadEnabled &&

                        <Button
                            id={`qle-document-choose-${applicantGuid}-${categoryType}-${documentType}`}
                            className='gi-button gi-button__outline pull-right'
                            disabled={isButtonDisabled}
                            onClick={onClickAction}
                            aria-labelledby={`qle-document-choose-${applicantGuid}-${categoryType}-${documentType} qle-document-desc-${applicantGuid}-${categoryType}-${documentType}`}
                        >
                            <FormattedMessage id='button.choose_this' />
                        </Button>
                    }
                </div>
            </div>
            { getDocumentInfo() }
        </li>
    );
};

export default DocumentItem;